import { createContext, useContext, useState, createElement } from 'react'
import type { ReactNode } from 'react'

export interface Hotel {
  id: string
  name: string
  location: string
  price: number
  rating: number
  description: string
  image: string
  amenities: string[]
}

export interface Room {
  id: string
  name: string
  priceModifier: number
  size: number
  maxGuests: number
  bedType: string
  amenities: string[]
  image: string
}

interface BookingState {
  location: string
  checkIn: Date | null
  checkOut: Date | null
  adults: number
  rooms: number
  selectedHotel: Hotel | null
  selectedRoom: Room | null
}

interface BookingContextType extends BookingState {
  setLocation: (location: string) => void
  setDates: (checkIn: Date | null, checkOut: Date | null) => void
  setGuests: (adults: number, rooms: number) => void
  selectHotel: (hotel: Hotel | null) => void
  selectRoom: (room: Room | null) => void
  resetBooking: () => void
}

const initialState: BookingState = {
  location: '',
  checkIn: null,
  checkOut: null,
  adults: 2,
  rooms: 1,
  selectedHotel: null,
  selectedRoom: null,
}

const BookingContext = createContext<BookingContextType | undefined>(undefined)

export function BookingProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<BookingState>(initialState)

  const setLocation = (location: string) =>
    setState((s) => ({ ...s, location }))
  const setDates = (checkIn: Date | null, checkOut: Date | null) =>
    setState((s) => ({ ...s, checkIn, checkOut }))
  const setGuests = (adults: number, rooms: number) =>
    setState((s) => ({ ...s, adults, rooms }))
  const selectHotel = (selectedHotel: Hotel | null) =>
    setState((s) => ({ ...s, selectedHotel }))
  const selectRoom = (selectedRoom: Room | null) =>
    setState((s) => ({ ...s, selectedRoom }))
  const resetBooking = () => setState(initialState)

  return createElement(
    BookingContext.Provider,
    {
      value: {
        ...state,
        setLocation,
        setDates,
        setGuests,
        selectHotel,
        selectRoom,
        resetBooking,
      },
    },
    children,
  )
}

export function useBooking() {
  const context = useContext(BookingContext)
  if (context === undefined) {
    throw new Error('useBooking must be used within a BookingProvider')
  }
  return context
}
